import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux";
import { AgentEnvironment, agentEnvironmentActions } from "../../redux/reducers/agentEnvironmentSlice";
import { View } from "../base";
import { Icon } from "../base/Icon";
import { Dialog } from "../base/Dialog";
import { InputField } from "../base/InputField";
import { SelectField } from "../base/SelectField";

type CreateAgentEnvironmentDialogProps = {
  agentId: string;
  isOpen?: boolean;
  onClose?: () => void;
};

export const CreateAgentEnvironmentDialog = ({ agentId, isOpen = false, onClose = () => {} }: CreateAgentEnvironmentDialogProps) => {
  const dispatch = useAppDispatch();
  const [newEnvironment, setNewEnvironment] = useState<Partial<AgentEnvironment>>({});
  const isLoading = useAppSelector((state) => state.agentEnvironment.isLoadingCreate);
  const agentFunctions = useAppSelector((state) => Object.values(state.agentFunction.entities).filter((env) => env.agentId === agentId));

  const onClickCreateEnvironment = async () => {
    await dispatch(
      agentEnvironmentActions.create({
        agentId,
        name: newEnvironment.name,
        functionId: newEnvironment.functionId || "",
      }),
    );

    onClose();
    setNewEnvironment({ name: "", functionId: "" });
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Create New Environment"
      className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"
    >
      <View className="space-y-4 p-4">
        <InputField
          label="Name"
          value={newEnvironment.name}
          onChange={(value) => setNewEnvironment({ ...newEnvironment, name: value })}
          placeholder="Enter environment name"
        />
        <SelectField
          label="Function"
          value={newEnvironment.functionId}
          placeholder="Select function"
          onChange={(value) => setNewEnvironment({ ...newEnvironment, functionId: value })}
          options={agentFunctions.map((agentFunction) => ({
            label: agentFunction.displayName || agentFunction.name,
            value: agentFunction.id,
          }))}
        />
        <View className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="flex flex-row items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors border bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={onClickCreateEnvironment}
            disabled={isLoading || !newEnvironment.name}
            className="flex flex-row items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors border bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Icon
              name="add"
              size={14}
            />
            {isLoading ? "Creating..." : "Create Environment"}
          </button>
        </View>
      </View>
    </Dialog>
  );
};
